import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Box,
  BoxProps,
  ClickAwayListener,
  Popper as MuiPopper,
  styled,
} from '@mui/material';

import errorSvg from '../assets/images/svg/ic_error_ifo.svg';
import SearchSvgIcon from './SvgIcon/SearchSvgIcon';
import { ErrorProps } from '../types';

interface ExternalContainerProps {
  disabled?: boolean;
}

const ExternalContainer = styled(Box)<ExternalContainerProps>`
  opacity: ${({ disabled }) => (disabled ? 0.9 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'initial')};
`;

interface ContainerProps {
  selected: boolean;
  popperOpen: boolean;
  error?: boolean;
  disabled?: boolean;
  mode?: 'dark' | 'white';
}

// prettier-ignore
const Container = styled(
  Box,
  { shouldForwardProp: (prop) => prop !== 'popperOpen' && prop !== 'error' && prop !== 'selected' }
)<ContainerProps>`
  ${({ theme }) => theme.typography.body2};
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 40px;
  opacity: ${({ disabled }) => (disabled ? "0.3" : "1")};
  color: ${({ theme }) => theme.color.secondary.$60};
  background: rgba(245, 245, 245, 1);
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ popperOpen, error, theme }) => {
    if (error) return theme.color.highlight;
    if (popperOpen) return 'rgba(0, 0, 0, 0.5)';
    return 'transparent';
  }};
  user-select: none;

  &:hover {
    border: 1px solid #ffb905;
    transition-duration: 0.3s;
  }

  span {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .btn-clear {
    cursor: pointer;
  }

  svg {
    display: block;
    margin: 4px;
    margin-right: 8px;
  }
`;

interface ItemProps {
  itemColor?: string;
  selected?: boolean;
  mode?: 'dark' | 'white';
}

const shouldForwardProp = (prop: PropertyKey) =>
  prop !== 'itemColor' && prop !== 'selected';

const SelectedItem = styled('span', { shouldForwardProp })<ItemProps>`
  flex: 1;
  display: flex;
  align-items: center;
  margin-left: 10px;
  color: ${({ theme, selected, mode }) =>
    mode ? (selected ? 'white' : '#A1A1A1') : theme.color.secondary.$60};
  white-space: nowrap;
  width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorContainer = styled(Box)`
  ${({ theme }) => theme.typography.caption};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.color.highlight};
`;

interface PopperProps {
  mode?: 'dark' | 'white';
}

const Popper = styled(MuiPopper)<PopperProps>`
  z-index: 2000;
  color: ${({ theme, mode }) => (mode ? 'white' : theme.color.secondary.$100)};
  & .container {
    max-height: 235px;
    background: ${({ mode }) => (mode ? 'rgb(62, 62, 62)' : 'white')};
    border-radius: 4px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }

  & .header {
    display: flex;
    align-items: center;
    background: ${({ mode }) => (mode ? 'rgba(0, 0, 0, 0.2)' : 'white')};
    padding: 4px 0;

    input {
      ${({ theme }) => theme.typography.body2};
      flex: 1;
      display: inline-block;
      background: unset;
      border: none;
      outline: none;
      padding: 6px 8px;
      color: ${({ theme, mode }) =>
        mode ? 'white' : theme.color.secondary.$100};
    }
  }

  & .menu {
    color: ${({ theme, mode }) =>
      mode ? 'white' : theme.color.secondary.$100};
    padding-left: ${({ mode }) => (mode ? '16px' : '0')};
    font-weight: ${({ mode }) => (mode ? '700' : 'auto')};
    max-height: 180px;
    overflow-y: auto;
    margin: 0;
  }
`;

// prettier-ignore
const Item = styled('li', { shouldForwardProp: (prop) => prop !== 'itemColor' } )<ItemProps>`
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  padding: 9.5px 10px;
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  &:before {
    display: ${({ itemColor }) => (itemColor ? 'block' : 'none')};
    content: '';
    width: 6px;
    height: 14px;
    margin-right: 8px;
    background: ${({ itemColor }) => (itemColor ? itemColor : 'unset')};
    border-radius: 3px;
  }
`;

interface Data {
  id: string;
  name: string;
  value?: string;
}

interface DropdownSearchProps extends Omit<BoxProps, 'onSelect' | 'onBlur'> {
  selectedId?: string;
  placeholder?: string;
  popperPlaceholder?: string;
  data: Array<Data>;
  keyPrefix?: string;
  error?: ErrorProps;
  disabled?: boolean;
  reset?: boolean;
  onSelect: (d: Data) => void;
  onBlur: () => void;
  mode?: 'dark' | 'white';
  currentSelectedName?: string;
}

const DropdownSearch: React.FC<DropdownSearchProps> = ({
  selectedId,
  placeholder,
  popperPlaceholder,
  data,
  error,
  keyPrefix = 'ds',
  disabled,
  onSelect,
  onBlur,
  mode,
  currentSelectedName,
  ...props
}) => {
  const externalContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [selectedData, setSelectedData] = useState<Data | undefined>(
    data.find((d) => d.name === currentSelectedName) ||
      data.find((d) => d.id === selectedId) ||
      undefined,
  );
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');

  // Sinkronisasi saat selectedId berubah dari luar
  useEffect(() => {
    if (!selectedId) return;
    const found = data.find((d) => d.id === selectedId);
    if (found) setSelectedData(found);
  }, [selectedId, data]);

  // Sinkronisasi saat currentSelectedName berubah dari luar
  useEffect(() => {
    if (!currentSelectedName) return;
    const found = data.find((d) => d.name === currentSelectedName);
    if (found) setSelectedData(found);
  }, [currentSelectedName, data]);

  // Focus ke input saat popper dibuka
  useEffect(() => {
    if (open) {
      // kecilkan jeda agar Popper sempat mount
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return;
  }, [open]);

  const filtered = useMemo(
    () =>
      data.filter((d) =>
        d.name.toLocaleLowerCase().includes(filterText.toLocaleLowerCase()),
      ),
    [data, filterText],
  );

  const closeAndBlur = () => {
    if (!open) return;
    setOpen(false);
    onBlur?.();
  };

  const handleOnSelect = (d: Data) => {
    setSelectedData(d);
    onSelect(d);
    setOpen(false);
    onBlur?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeAndBlur();
    }
  };

  return (
    <ClickAwayListener
      // Pastikan click-away berlaku untuk anchor + popper
      onClickAway={closeAndBlur}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <Box onKeyDown={handleKeyDown}>
        <ExternalContainer
          ref={externalContainerRef}
          disabled={disabled}
          {...props}
        >
          <Container
            role="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            tabIndex={0}
            selected={!!selectedData}
            popperOpen={open}
            disabled={disabled}
            error={error?.isError}
            onClick={() => !disabled && setOpen(true)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(true);
              }
            }}
          >
            <SelectedItem
              itemColor={selectedData?.value}
              selected={!!selectedData}
              mode={mode}
            >
              {selectedData ? selectedData.name : placeholder}
            </SelectedItem>
            {open ? (
              <KeyboardArrowUp
                sx={{
                  color: selectedData ? '#fff' : '#A1A1A1',
                  width: 24,
                  height: 24,
                  marginRight: '10px !important',
                }}
              />
            ) : (
              <KeyboardArrowDown
                sx={{
                  color: selectedData ? '#fff' : '#A1A1A1',
                  width: 24,
                  height: 24,
                  marginRight: '10px !important',
                }}
              />
            )}
          </Container>
          {error?.isError && (
            <ErrorContainer>
              <img src={errorSvg} alt="" /> {error.message}
            </ErrorContainer>
          )}
        </ExternalContainer>

        <Popper
          open={open}
          anchorEl={externalContainerRef.current}
          placement="bottom"
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: { offset: [0, 4] },
            },
          ]}
          mode={mode}
        >
          <div
            className="container"
            style={{
              width: `${externalContainerRef.current?.clientWidth || 240}px`,
            }}
            onMouseDown={(e) => {
              // biar klik di dalam popper tidak dianggap away
              e.stopPropagation();
            }}
          >
            <div className="header">
              <SearchSvgIcon sx={{ width: 32, height: 32, mr: 1 }} />
              <input
                ref={inputRef}
                placeholder={popperPlaceholder}
                value={filterText}
                onChange={(e) => setFilterText(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    closeAndBlur();
                  }
                }}
              />
            </div>
            <ul className="menu" role="listbox">
              {filtered.map((d) => (
                <Item
                  key={`${keyPrefix}-OrgAutocomplete-item-${d.id}`}
                  className="item"
                  itemColor={d.value}
                  onMouseDown={(e) => {
                    // mousedown supaya tidak kalah cepat oleh click-away
                    e.stopPropagation();
                    handleOnSelect(d);
                  }}
                >
                  {d.name}
                </Item>
              ))}
              {filtered.length === 0 && (
                <Item
                  as="div"
                  itemColor={undefined}
                  style={{ opacity: 0.6, cursor: 'default' }}
                >
                  No results
                </Item>
              )}
            </ul>
          </div>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default DropdownSearch;
